// src/pages/FinanceMonitoring.jsx
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Nav, ProgressBar, Row, Spinner, Alert, Dropdown } from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import ReactApexChart from "react-apexcharts";
import { formatNumber, formatCurrency } from "../utils/formatters";
import { exportCAtoExcel } from "../utils/excelExport1";
import { generateCAPDF } from "../utils/pdfExport1";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function FinanceMonitoring() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchDashboardStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/stats_dashboard.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur de chargement des données");
            const data = await response.json();
            setStats(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       CALCULS
       ============================================================ */
    const getTotalVentes = () => {
        if (!stats) return 0;
        if (stats.ca_annuel?.[0]?.nombre_factures) return stats.ca_annuel[0].nombre_factures;
        if (stats.top_clients) return stats.top_clients.reduce((acc, client) => acc + (client.nb_factures || 0), 0);
        return 0;
    };

    const getLastMonthlyCA = () => {
        if (!stats?.ca_mensuel) return 0;
        for (let i = stats.ca_mensuel.length - 1; i >= 0; i--) {
            if (stats.ca_mensuel[i] > 0) return stats.ca_mensuel[i];
        }
        return 0;
    };

    const totalVentes = getTotalVentes();
    const dernierCaMensuel = getLastMonthlyCA();
    const pourcentageObjectif = dernierCaMensuel > 0 ? Math.round((dernierCaMensuel / 5000000) * 100) : 0;

    const chartColors = {
        primary: COLORS.primary,
        secondary: COLORS.secondary,
        success: COLORS.success,
        danger: COLORS.danger,
        warning: COLORS.warning,
        info: COLORS.info,
        purple: COLORS.purple || '#8b5cf6',
        cyan: COLORS.cyan || '#06b6d4',
        palette: [
            COLORS.primary,
            COLORS.secondary,
            COLORS.success,
            COLORS.warning,
            COLORS.purple || '#8b5cf6',
            COLORS.cyan || '#06b6d4',
            COLORS.info,
            COLORS.danger
        ]
    };

    /* ============================================================
       GRAPHIQUES
       ============================================================ */
    const seriesCA = [{
        name: 'Chiffre d\'affaires',
        data: stats?.ca_mensuel || Array(12).fill(0)
    }];

    const optionsCA = {
        chart: { type: 'area', height: 350, toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'Inter, system-ui, sans-serif' },
        colors: [chartColors.primary],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.2 } },
        stroke: { curve: 'smooth', width: 2 },
        grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
        xaxis: { categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'], labels: { style: { fontSize: '11px', fontWeight: 500 } } },
        yaxis: { labels: { formatter: (value) => formatCurrency(value), style: { fontSize: '11px' } } },
        tooltip: { y: { formatter: (value) => formatCurrency(value) } }
    };

    const seriesIncomeExpenses = [
        { name: 'Revenus', type: 'column', data: stats?.income_expenses?.map(item => parseFloat(item.revenus)) || [] },
        { name: 'Dépenses', type: 'column', data: stats?.income_expenses?.map(item => parseFloat(item.depenses)) || [] }
    ];

    const optionsIncomeExpenses = {
        chart: { stacked: true, toolbar: { show: false }, height: 350, fontFamily: 'Inter, system-ui, sans-serif' },
        colors: [chartColors.primary, chartColors.secondary],
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 0 } },
        grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
        xaxis: {
            categories: stats?.income_expenses?.map(item => {
                const [year, month] = item.mois.split('-');
                const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
                return `${months[parseInt(month) - 1]} ${year}`;
            }) || [],
            labels: { style: { fontSize: '10px', fontWeight: 500 } }
        },
        yaxis: { labels: { formatter: (value) => formatCurrency(value), style: { fontSize: '11px' } } }
    };

    const seriesCategories = stats?.ventes_categories?.map(item => parseFloat(item.montant_total)) || [];
    const optionsDonut = {
        chart: { type: 'donut', height: 280, fontFamily: 'Inter, system-ui, sans-serif' },
        labels: stats?.ventes_categories?.map(item => item.nom_categorie || 'Sans catégorie') || ['Aucune donnée'],
        colors: chartColors.palette,
        legend: { position: 'bottom', fontSize: '11px', offsetY: 5 },
        tooltip: { y: { formatter: (value) => formatCurrency(value || 0) } },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: '14px',
                            fontWeight: 600,
                            formatter: () => formatCurrency(seriesCategories.reduce((a, b) => (a || 0) + (b || 0), 0))
                        }
                    }
                }
            }
        }
    };

    /* ============================================================
       EXPORTS
       ============================================================ */
    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const exportData = {
                periode: { annee: selectedYear, mois: new Date().toLocaleString('fr-FR', { month: 'long' }) },
                totaux: {
                    ca_annuel: stats.totaux?.ca_annuel || 0,
                    ca_mensuel: dernierCaMensuel || 0,
                    nb_clients: stats.totaux?.nb_clients || 0,
                    nb_fournisseurs: stats.totaux?.nb_fournisseurs || 0,
                    nb_produits: stats.totaux?.nb_produits || 0,
                    creances_total: stats.totaux?.creances_total || 0
                },
                ca_mensuel: stats.ca_mensuel || Array(12).fill(0),
                mois: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
                top_produits: stats.top_produits || [],
                top_clients: stats.top_clients || [],
                ventes_categories: stats.ventes_categories || [],
                income_expenses: stats.income_expenses || [],
                stock_alertes: stats.stock_alertes || [],
                profit_margins: stats.profit_margins || {},
                ratios: stats.ratios || {}
            };
            const success = await exportCAtoExcel(exportData, selectedYear);
            if (!success) alert("Erreur lors de l'export Excel");
        } catch (error) {
            console.error("Erreur export Excel:", error);
            alert("Erreur lors de l'export Excel: " + error.message);
        } finally {
            setExporting(false);
        }
    };

    const handleExportPDF = async () => {
        setExporting(true);
        try {
            const exportData = {
                titre: "Rapport Financier",
                periode: { annee: selectedYear, mois: new Date().toLocaleString('fr-FR', { month: 'long' }) },
                totaux: {
                    ca_annuel: stats.totaux?.ca_annuel || 0,
                    ca_mensuel: dernierCaMensuel || 0,
                    nb_clients: stats.totaux?.nb_clients || 0,
                    nb_fournisseurs: stats.totaux?.nb_fournisseurs || 0,
                    nb_produits: stats.totaux?.nb_produits || 0,
                    creances_total: stats.totaux?.creances_total || 0
                },
                top_produits: stats.top_produits || [],
                top_clients: stats.top_clients || [],
                ca_mensuel: stats.ca_mensuel || Array(12).fill(0),
                ventes_categories: stats.ventes_categories || [],
                income_expenses: stats.income_expenses || [],
                stock_alertes: stats.stock_alertes || []
            };
            await generateCAPDF(exportData, selectedYear);
        } catch (error) {
            console.error("Erreur export PDF:", error);
            alert("Erreur lors de l'export PDF: " + error.message);
        } finally {
            setExporting(false);
        }
    };

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
     if (loading) {
              
                      return (
                          <>
                              <Header />
                              <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                                      <div className="text-center" style={{ maxWidth: '400px' }}>
                                          <div className="loading-ring-wrapper mb-4">
                                              <div className="loading-ring"></div>
                                              <div className="loading-ring-inner"></div>
                                              <i className="ri-loader-4-fill loading-icon"></i>
                                          </div>
                                          <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                              Chargement des données...
                                          </h5>
                                          <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                          <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                              <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                                  <div
                                                      className="progress-bar progress-bar-striped progress-bar-animated"
                                                      style={{
                                                          width: '100%',
                                                          background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                          backgroundSize: '200% 100%',
                                                          animation: 'shimmer 1.5s linear infinite'
                                                      }}
                                                  />
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                              <Footer />
                              <style>{`
                                  .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                                  .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                                  .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                                  .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                                  @keyframes spin { to { transform: rotate(360deg); } }
                                  @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                                  @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                              `}</style>
                          </>
                      );
                  }
    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchDashboardStats} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bar-chart-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Tableau de bord financier</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-money-euro-circle-line"></i> CA {formatCurrency(parseFloat(stats.totaux?.ca_annuel) || 0)}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-shopping-bag-3-line"></i> {totalVentes} ventes
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-stack-line"></i> {stats.totaux?.nb_produits || 0} produits
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Dropdown align="end">
                                <Dropdown.Toggle className="btn-modern-outline" disabled={exporting}>
                                    {exporting ? <Spinner size="sm" className="me-1" /> : <i className="ri-download-line me-1"></i>} Exporter
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="dropdown-menu-charte">
                                    <Dropdown.Item onClick={handleExportExcel}>
                                        <i className="ri-file-excel-line me-2" style={{ color: COLORS.success }}></i> Export Excel
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleExportPDF}>
                                        <i className="ri-file-pdf-line me-2" style={{ color: COLORS.danger }}></i> Export PDF
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                            <Button className="btn-modern-primary" onClick={fetchDashboardStats}>
                                <i className="ri-refresh-line me-1"></i> Actualiser
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">CA annuel</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(parseFloat(stats.totaux?.ca_annuel) || 0)}</h3>
                                    <span className="kpi-unit-charte">{stats.ca_annuel?.[0]?.nombre_factures || totalVentes} factures</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-shopping-bag-3-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Ventes totales</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.success }}>{totalVentes}</h3>
                                    <span className="kpi-unit-charte">CA: {formatCurrency(parseFloat(stats.totaux?.ca_annuel) || 0)}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-group-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Clients actifs</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.warning }}>{stats.totaux?.nb_clients || 0}</h3>
                                    <span className="kpi-unit-charte">Fournisseurs: {stats.totaux?.nb_fournisseurs || 0}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Produits en stock</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.info }}>{stats.totaux?.nb_produits || 0}</h3>
                                    <span className="kpi-unit-charte">Top ventes: {stats.top_produits?.length || 0}</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== OBJECTIF MENSUEL ===== */}
                    <Card className="objectif-card-charte mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-3">
                                <Col md="8">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="objectif-icon-charte">
                                            <i className="ri-focus-2-line"></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="small fw-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                                                    Objectif mensuel
                                                </span>
                                                <span className="objectif-pourcentage-charte">{pourcentageObjectif}%</span>
                                            </div>
                                            <div className="progress-objectif-charte">
                                                <div
                                                    className="progress-bar-objectif-charte"
                                                    style={{ width: `${Math.min(pourcentageObjectif, 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="text-md-end text-center">
                                        <span className="small d-block" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                            Objectif 5 000 000 FCFA
                                        </span>
                                        <span className="fw-bold" style={{ color: 'white', fontSize: '1.3rem' }}>
                                            {formatCurrency(dernierCaMensuel)}
                                        </span>
                                        <small className="d-block" style={{ color: 'rgba(255,255,255,0.6)' }}>
                                            (dernier mois)
                                        </small>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== GRAPHIQUE CA + DONUT ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="8">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-bar-chart-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">Évolution du chiffre d'affaires</h6>
                                        </div>
                                        <div className="year-selector-charte">
                                            <button
                                                className={`year-btn-charte ${selectedYear === 2026 ? 'active' : ''}`}
                                                onClick={() => setSelectedYear(2026)}
                                            >
                                                2026
                                            </button>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <ReactApexChart series={seriesCA} options={optionsCA} type="area" height={330} />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl="4">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-pie-chart-2-line text-white"></i>
                                        <h6 className="fw-semibold mb-0 text-white">Répartition des ventes</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {seriesCategories.length > 0 && seriesCategories.reduce((a, b) => a + b, 0) > 0 ? (
                                        <ReactApexChart series={seriesCategories} options={optionsDonut} type="donut" height={280} />
                                    ) : (
                                        <div className="empty-state-charte">
                                            <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}15, ${COLORS.secondary}05)`, color: COLORS.secondary }}>
                                                <i className="ri-pie-chart-2-line"></i>
                                            </div>
                                            <h6 className="fw-semibold mb-1">Aucune donnée</h6>
                                            <p className="text-muted small mb-0">Aucune vente catégorisée disponible</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== REVENUS VS DÉPENSES + MARGES ===== */}
                    {stats.income_expenses && stats.income_expenses.length > 0 && (
                        <Row className="g-3 mb-4">
                            <Col xl="7" className="d-flex">
                                <Card className="border-0 shadow-sm rounded-3 w-100">
                                    <Card.Header className="card-header-gradient">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-bar-chart-grouped-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">Revenus vs Dépenses</h6>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-3">
                                        <ReactApexChart series={seriesIncomeExpenses} options={optionsIncomeExpenses} type="bar" height={340} />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xl="5" className="d-flex">
                                <Card className="border-0 shadow-sm rounded-3 w-100">
                                    <Card.Header className="card-header-gradient-secondary">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-line-chart-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">Marges bénéficiaires</h6>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {/* Marge brute */}
                                        <div className="marge-item-charte">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="marge-label-charte">Marge brute</span>
                                                <span className="marge-value-charte" style={{ color: COLORS.primary }}>
                                                    {stats.profit_margins?.gross || 0}%
                                                </span>
                                            </div>
                                            <div className="marge-bar-charte">
                                                <div
                                                    className="marge-bar-fill-charte"
                                                    style={{
                                                        width: `${stats.profit_margins?.gross || 0}%`,
                                                        background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.primaryDark})`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        {/* Marge opérationnelle */}
                                        <div className="marge-item-charte">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="marge-label-charte">Marge opérationnelle</span>
                                                <span className="marge-value-charte" style={{ color: COLORS.secondary }}>
                                                    {stats.profit_margins?.operating || 0}%
                                                </span>
                                            </div>
                                            <div className="marge-bar-charte">
                                                <div
                                                    className="marge-bar-fill-charte"
                                                    style={{
                                                        width: `${stats.profit_margins?.operating || 0}%`,
                                                        background: `linear-gradient(90deg, ${COLORS.secondary}, #0d8ba6)`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        {/* Marge nette */}
                                        <div className="marge-item-charte">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="marge-label-charte">Marge nette</span>
                                                <span className="marge-value-charte" style={{ color: COLORS.success }}>
                                                    {stats.profit_margins?.net || 0}%
                                                </span>
                                            </div>
                                            <div className="marge-bar-charte">
                                                <div
                                                    className="marge-bar-fill-charte"
                                                    style={{
                                                        width: `${stats.profit_margins?.net || 0}%`,
                                                        background: `linear-gradient(90deg, ${COLORS.success}, #059669)`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />

                                        <Row className="g-2">
                                            <Col xs="6">
                                                <div className="ratio-box-charte" style={{ background: `${COLORS.primary}10` }}>
                                                    <h6 className="mb-0 fw-bold" style={{ fontSize: '1rem', color: COLORS.primary }}>
                                                        {stats.ratios?.quick_ratio || 0}
                                                    </h6>
                                                    <small style={{ color: COLORS.primary, fontSize: '0.65rem', fontWeight: '600' }}>
                                                        Quick Ratio
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col xs="6">
                                                <div className="ratio-box-charte" style={{ background: `${COLORS.secondary}10` }}>
                                                    <h6 className="mb-0 fw-bold" style={{ fontSize: '1rem', color: COLORS.secondary }}>
                                                        {stats.ratios?.current_ratio || 0}
                                                    </h6>
                                                    <small style={{ color: COLORS.secondary, fontSize: '0.65rem', fontWeight: '600' }}>
                                                        Current Ratio
                                                    </small>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* ===== TOP PRODUITS & TOP CLIENTS ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="6" className="d-flex">
                            <Card className="border-0 shadow-sm rounded-3 w-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-stack-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">
                                                Top {stats.top_produits?.length || 3} Produits / services
                                            </h6>
                                        </div>
                                        <span className="badge-header-light">
                                            <i className="ri-shopping-bag-3-line"></i> Ventes
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <table className="table-charte mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">Désignation</th>
                                                    <th className="text-end">Qté</th>
                                                    <th className="text-end pe-4">Montant</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.top_produits?.map((produit, index) => (
                                                    <tr key={index}>
                                                        <td className="ps-4 fw-semibold" style={{ color: COLORS.primaryDark }}>
                                                            {produit.designation}
                                                        </td>
                                                        <td className="text-end">{formatNumber(parseInt(produit.quantite_vendue))}</td>
                                                        <td className="text-end pe-4 fw-bold" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(parseFloat(produit.montant_total))}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl="6" className="d-flex">
                            <Card className="border-0 shadow-sm rounded-3 w-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-group-line text-white"></i>
                                            <h6 className="fw-semibold mb-0 text-white">
                                                Top {stats.top_clients?.length || 3} Clients
                                            </h6>
                                        </div>
                                        <span className="badge-header-light">
                                            <i className="ri-money-euro-circle-line"></i> CA
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <table className="table-charte mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">Client</th>
                                                    <th className="text-end">Factures</th>
                                                    <th className="text-end pe-4">Total achats</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.top_clients?.map((client, index) => (
                                                    <tr key={index}>
                                                        <td className="ps-4 fw-semibold" style={{ color: COLORS.primaryDark }}>
                                                            {client.raison_sociale}
                                                        </td>
                                                        <td className="text-end">{client.nb_factures}</td>
                                                        <td className="text-end pe-4 fw-bold" style={{ color: COLORS.secondary }}>
                                                            {formatCurrency(parseFloat(client.total_achats))}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== OBJECTIF CARD ===== */
                .objectif-card-charte {
                    background: linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.secondary});
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.25);
                    overflow: hidden;
                    position: relative;
                }
                .objectif-icon-charte {
                    width: 52px; height: 52px; border-radius: 14px;
                    background: rgba(255,255,255,0.15);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                    flex-shrink: 0;
                }
                .objectif-pourcentage-charte {
                    background: white; color: var(--primary);
                    padding: 0.2rem 0.75rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 700;
                }
                .progress-objectif-charte {
                    height: 8px; border-radius: 6px;
                    background: rgba(255,255,255,0.2);
                    overflow: hidden;
                }
                .progress-bar-objectif-charte {
                    height: 100%; border-radius: 6px;
                    background: linear-gradient(90deg, #ffffff, rgba(255,255,255,0.7));
                    transition: width 0.6s ease;
                    box-shadow: 0 2px 8px rgba(255,255,255,0.3);
                }

                /* ===== HEADERS GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .card-header-gradient-secondary {
                    background: linear-gradient(135deg, var(--secondary) 0%, #0d8ba6 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.7rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                /* ===== YEAR SELECTOR ===== */
                .year-selector-charte {
                    display: inline-flex; background: rgba(255,255,255,0.15);
                    border-radius: 50px; padding: 3px;
                }
                .year-btn-charte {
                    border: none; background: transparent;
                    padding: 0.3rem 0.9rem;
                    border-radius: 50px;
                    font-size: 0.7rem; font-weight: 600;
                    color: rgba(255,255,255,0.7);
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .year-btn-charte:hover { color: white; }
                .year-btn-charte.active {
                    background: white; color: var(--primary);
                    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
                }

                /* ===== MARGES ===== */
                .marge-item-charte { margin-bottom: 1.25rem; }
                .marge-item-charte:last-of-type { margin-bottom: 0; }
                .marge-label-charte {
                    font-size: 0.8rem; color: #64748b; font-weight: 500;
                }
                .marge-value-charte {
                    font-size: 0.85rem; font-weight: 700;
                }
                .marge-bar-charte {
                    height: 6px; border-radius: 4px;
                    background: #f1f5f9;
                    overflow: hidden;
                }
                .marge-bar-fill-charte {
                    height: 100%; border-radius: 4px;
                    transition: width 0.6s ease;
                }

                /* ===== RATIOS ===== */
                .ratio-box-charte {
                    padding: 0.85rem;
                    border-radius: 12px;
                    text-align: center;
                    transition: all 0.2s ease;
                }
                .ratio-box-charte:hover {
                    transform: translateY(-2px);
                }

                /* ===== TABLE ===== */
                .table-charte {
                    width: 100%;
                    border-collapse: collapse;
                }
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr:last-child td { border-bottom: none; }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem;
                }

                /* ===== DROPDOWN ===== */
                .dropdown-menu-charte {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.12);
                    padding: 0.5rem;
                }
                .dropdown-menu-charte .dropdown-item {
                    border-radius: 8px;
                    padding: 0.5rem 0.75rem;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                }
                .dropdown-menu-charte .dropdown-item:hover {
                    background: #f1f5f9;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }

                /* ===== ALERTE ERREUR ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .objectif-card-charte .text-md-end { text-align: center !important; margin-top: 1rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </React.Fragment>
    );
}